import { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const treatments = [
  {
    id: 'consulta',
    name: 'Consulta Ginecológica',
    desc: 'Atendimento completo e personalizado para cuidar da sua saúde íntima em todas as fases da vida, com escuta ativa e olhar integral.',
    image: 'https://storage.helloreaddy.io/project_files/7ae9a526-920c-4103-99ac-b845e125a623/4f667703-c5fd-4905-99de-92b4e67efbf8_compressed_Captura-de-tela-2026-07-22-154607.webp',
  },
  {
    id: 'preventivo',
    name: 'Preventivo',
    desc: 'Exame essencial para a detecção precoce de alterações no colo do útero, realizado com máximo conforto e respeito ao seu corpo.',
    image: 'https://readdy.ai/api/search-image?query=Delicate%20closeup%20of%20female%20hands%20gently%20holding%20a%20fresh%20white%20peony%20and%20eucalyptus%20branch%20on%20a%20cream%20marble%20surface%2C%20soft%20golden%20natural%20window%20light%2C%20blush%20and%20cream%20tone%20background%2C%20gentle%20feminine%20aesthetic%2C%20editorial%20composition%2C%20shallow%20depth%20of%20field%2C%20calm%20and%20trusting%20mood%2C%20film%20grain&width=800&height=1100&seq=area-card-preventivo-v4&orientation=portrait',
  },
  {
    id: 'cirurgia-intima',
    name: 'Cirurgia Íntima',
    desc: 'Procedimentos delicados para correção funcional e estética da região íntima feminina, com foco em conforto, segurança e bem-estar.',
    image: 'https://static.readdy.ai/image/7c1e788bcb66949f87d807c4f125e261/7156eb75f8a583ff87912235a9ae6ccb.jpeg',
  },
  {
    id: 'laser-intimo',
    name: 'Laser Íntimo',
    desc: 'Tratamento a laser de última geração para rejuvenescimento e saúde íntima, proporcionando mais conforto e autoestima.',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20soft%20light%20refraction%20through%20crystal%20glass%20prism%20on%20cream%20surface%2C%20warm%20beige%20and%20blush%20tones%2C%20elegant%20minimal%20composition%2C%20feminine%20energy%2C%20modern%20luxury%20aesthetic%2C%20editorial%20photography%2C%20clean%20and%20refined%2C%20soft%20bokeh%20effect%2C%20warm%20ambient%20light&width=800&height=1100&seq=area-card-laser-v4&orientation=portrait',
  },
  {
    id: 'implantes',
    name: 'Implantes Hormonais',
    desc: 'Tecnologia inovadora em contracepção e terapia hormonal, com implantes subdérmicos que oferecem liberdade e tranquilidade.',
    image: 'https://storage.helloreaddy.io/project_files/7ae9a526-920c-4103-99ac-b845e125a623/dd448495-709c-4634-9ab8-6193f7e15d7b_compressed_IMPLANTES.webp',
  },
  {
    id: 'reposicao',
    name: 'Reposição Hormonal',
    desc: 'Acompanhamento personalizado para mulheres no climatério e menopausa, com reposição hormonal bioidêntica respeitando sua individualidade.',
    image: 'https://storage.helloreaddy.io/project_files/7ae9a526-920c-4103-99ac-b845e125a623/bc6b5614-c3f8-476d-b6d3-d989352151b9_compressed_REPOSICAO-HORMONAL.webp',
  },
  {
    id: 'menopausa',
    name: 'Menopausa',
    desc: 'Abordagem moderna e acolhedora para esta fase natural da vida, com tratamentos que aliviam sintomas e promovem bem-estar.',
    image: 'https://storage.helloreaddy.io/project_files/7ae9a526-920c-4103-99ac-b845e125a623/956aec57-ed0e-4d18-8c99-7755b1c35af8_compressed_MENOPAUSA.webp',
  },
  {
    id: 'saude-sexual',
    name: 'Saúde Sexual',
    desc: 'Espaço seguro e sem tabus para dialogar sobre sexualidade feminina, libido e prazer, com orientação profissional respeitosa.',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20soft%20textures%20of%20flowing%20cream%20silk%20fabric%20in%20gentle%20breeze%2C%20warm%20natural%20light%2C%20blush%20and%20rose%20undertones%2C%20feminine%20and%20elegant%20composition%2C%20editorial%20art%20photography%2C%20sensual%20yet%20refined%2C%20organic%20shapes%2C%20warm%20ambient%20atmosphere&width=800&height=1100&seq=area-card-saude-v4&orientation=portrait',
  },
];

export default function AreasSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll-triggered entrance for section header
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const st = ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      once: true,
      onEnter: () => setIsVisible(true),
    });

    return () => { st.kill(); };
  }, []);

  // Keyboard navigation (desktop)
  const handleKeyNav = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      setHoveredIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, treatments.length - 1)));
    } else if (e.key === 'ArrowLeft') {
      setHoveredIndex((prev) => (prev === null || prev === 0 ? null : prev - 1));
    }
  }, []);

  return (
    <section
      id="areas"
      ref={sectionRef}
      className="relative w-full bg-nude overflow-hidden"
      tabIndex={0}
      onKeyDown={handleKeyNav}
      aria-label="Áreas de atuação da Dra. Nathália Posso"
    >
      {/* Divider top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-matte/45 to-transparent z-10" />

      {/* Header */}
      <div className="text-center pt-16 md:pt-20 pb-8 md:pb-14 px-6">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="h-px w-8 bg-gold-matte/40" />
          <span className="font-body text-[10px] md:text-[11px] text-secondary-500 tracking-[0.34em] uppercase">
            Áreas de Atuação
          </span>
          <span className="h-px w-8 bg-gold-matte/40" />
        </div>
        <h2
          className={`font-heading text-2xl md:text-3xl lg:text-4xl text-graphite font-light max-w-xl mx-auto leading-[1.2] transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          Excelência em cada{' '}
          <span className="italic">detalhe</span> do seu cuidado.
        </h2>
        <p
          className={`font-body text-sm text-graphite/45 mt-4 max-w-md mx-auto transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          Tratamento integral — a causa, não o sintoma isolado.
        </p>
      </div>

      {/* Desktop: Accordion Cards */}
      <div
        ref={cardsRef}
        className="flex h-[380px] sm:h-[440px] md:h-[520px] lg:h-[560px] gap-1 md:gap-2 px-2 sm:px-4 md:px-6 lg:px-10 pb-8 md:pb-12 items-stretch justify-center max-w-[1400px] mx-auto"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {treatments.map((treatment, index) => {
          const isHovered = hoveredIndex === index;
          const isAnyHovered = hoveredIndex !== null;

          return (
            <div
              key={treatment.id}
              className="group relative overflow-hidden rounded-2xl border border-gold-matte/12 cursor-pointer"
              style={{
                flex: isHovered ? '3.2' : isAnyHovered ? '0.7' : '1',
                transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onClick={() => setHoveredIndex(index)}
              role="button"
              tabIndex={0}
              aria-label={treatment.name}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setHoveredIndex(index);
                }
              }}
            >
              {/* Background image */}
              <div
                className="absolute inset-0 transition-opacity duration-700 ease-out"
                style={{
                  opacity: isHovered ? 0.35 : 0.12,
                }}
              >
                <img
                  src={treatment.image}
                  alt=""
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>

              {/* Gradient overlays */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-offwhite via-offwhite/90 to-offwhite/60"
                style={{ opacity: isHovered ? 0.85 : 0.95 }}
              />
              <div
                className="absolute inset-0 bg-gradient-to-b from-offwhite/50 to-transparent"
                style={{ opacity: isHovered ? 0.4 : 0.7 }}
              />

              {/* Number badge */}
              <span
                className="absolute left-3 md:left-5 top-3 md:top-5 z-10 font-body text-[10px] md:text-xs tabular-nums tracking-[0.1em] transition-colors duration-500"
                style={{
                  color: isHovered ? 'rgba(196, 166, 117, 0.85)' : 'rgba(60, 52, 44, 0.35)',
                }}
              >
                {String(index + 1).padStart(2, '0')}
              </span>

              {/* Collapsed: vertical title */}
              <div
                className="absolute inset-0 flex items-center justify-center z-10 transition-all duration-700 ease-out"
                style={{
                  opacity: isHovered ? 0 : 1,
                  transform: isHovered ? 'translateY(20px)' : 'translateY(0)',
                }}
              >
                <span
                  className="whitespace-nowrap font-heading text-sm sm:text-base md:text-lg lg:text-xl leading-none text-graphite/40 group-hover:text-graphite/60 transition-colors duration-500"
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    letterSpacing: '0.05em',
                  }}
                >
                  {treatment.name}
                </span>
              </div>

              {/* Expanded: title + desc + link */}
              <div
                className="absolute inset-0 z-10 flex flex-col justify-end p-4 sm:p-5 md:p-6 lg:p-8 transition-all duration-500"
                style={{
                  opacity: isHovered ? 1 : 0,
                  transform: isHovered ? 'translateY(0)' : 'translateY(16px)',
                }}
              >
                <h3 className="font-heading text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-medium leading-tight text-graphite">
                  {treatment.name}
                </h3>
                <p className="mt-2 md:mt-3 max-w-sm font-body text-xs sm:text-sm lg:text-[15px] leading-[1.7] text-graphite/60">
                  {treatment.desc}
                </p>
              </div>

              {/* Bottom accent line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-700 ease-out bg-gradient-to-r from-transparent via-gold-matte/70 to-transparent"
                style={{
                  opacity: isHovered ? 1 : 0.25,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Removed mobile editorial cards — accordion now works on all screen sizes */}

      {/* "Também atuamos em" row */}
      <div
        className={`flex flex-col items-center gap-5 border-t border-gold-matte/10 pt-8 pb-14 md:pb-16 px-6 text-center mx-auto max-w-3xl transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <span className="font-body text-[10px] md:text-[11px] text-secondary-500 tracking-[0.34em] uppercase">
          Também tratamos
        </span>
        <div className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-2 font-heading text-lg text-graphite/55 sm:text-xl">
          <span className="hover:text-gold-matte transition-colors duration-300 cursor-default">Laser Íntimo</span>
          <span className="text-gold-matte/30">·</span>
          <span className="hover:text-gold-matte transition-colors duration-300 cursor-default">Clareamento Intimo</span>
          <span className="text-gold-matte/30">·</span>
          <span className="hover:text-gold-matte transition-colors duration-300 cursor-default">Cirurgia Intima</span>
          <span className="text-gold-matte/30">·</span>
          <span className="hover:text-gold-matte transition-colors duration-300 cursor-default">Acompanhamento Pré-Natal</span>
          <span className="text-gold-matte/30">·</span>
          <span className="hover:text-gold-matte transition-colors duration-300 cursor-default">Infertilidade</span>
        </div>
      </div>

      {/* Divider bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-matte/45 to-transparent z-10" />
    </section>
  );
}